import React from 'react';

import { NativeResources } from '@ariksa/inventory-core/api';
import { Box, Center, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { map } from 'lodash';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-use';

import {
  renderContextTags,
  renderTime,
  renderTooltipFieldValue,
  renderRiskContext,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import {
  CheckmarkCircleOutlineIcon,
  ExceptionIcon,
  LinkIcon,
  SearchIcon,
  TagIcon,
} from 'components/Icons';
import { TableActionsMenu } from 'components/Overlay';
import { successToast } from 'components/Toast';
import { useMetadataDrawerTabs } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/hooks/useMetadataDrawerTabs';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import { ResourceTypeIconTooltip } from 'containers/App/hooks/useResourceType';
import { selectApp } from 'containers/App/selectors';
import { renderVulnerabilities } from 'containers/Inventory/CloudInventory/Components/utils';
import { useInventoryContext } from 'containers/Inventory/CloudInventory/hooks/context';
import { selectInventory } from 'containers/Inventory/CloudInventory/selector';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';
import { renderResourceState } from 'containers/Visibility/Vulnerabilities/Components/ResourceVulnerabilities/utils';

export const useInstanceTableColumns = () => {
  const {
    instanceResourceType,
    currentCategory,
    instanceAnalysisSummary,
    instanceInventory,
  } = useSelector(selectInventory);
  const { renderStackedAccountNameID } = useCloudAccountId();
  const { data: summary } = instanceAnalysisSummary;
  const { getTabIndex } = useMetadataDrawerTabs();
  const { updateActiveResource } = useActiveResourceActions();
  const { onOpenMetadata } = useActiveResourceContext();
  const { user: userInfo } = useSelector(selectApp);
  const { isDevOrTest, riskContext: sharedRiskContext } = useSelector(
    selectSharedState,
  );

  const { setCurrentInstance, setIsOpenAddContext } = useInventoryContext();

  const openWhitelist = useDisclosure();
  const summarizeResource = useDisclosure();
  const location = useLocation();
  const statusDisclosure = useDisclosure();

  const onClickActionButton = (e, row) => {
    e.stopPropagation();
    setCurrentInstance(row);
  };

  const renderActions = ({ row }) => {
    const items = [
      {
        label: 'Configure Allowed List',
        icon: <CheckmarkCircleOutlineIcon />,
        onClick: e => {
          onClickActionButton(e, row);
          openWhitelist.onOpen();
        },
      },
      {
        label: 'Copy Resource Link',
        icon: <LinkIcon />,
        onClick: e => {
          onClickActionButton(e, row);
          navigator.clipboard.writeText(
            (
              location.origin +
              '/inventory/cloud/resource/' +
              row?.native_name +
              '/' +
              row.uuid +
              '?metadata_tab=' +
              MetadataTabs.Summary +
              '&organization=' +
              userInfo.info.org_id
            ).toString(),
          );
          successToast({
            title: 'Resource link copied!',
            duration: 1500,
          });
        },
      },
      ...(instanceResourceType === NativeResources.Ec2Instance ||
      instanceResourceType === NativeResources.LambdaFunction ||
      instanceResourceType === NativeResources.AmazonMachineImage ||
      instanceResourceType === NativeResources.EcsCluster
        ? [
            {
              label: 'Scan now',
              onClick: () => {
                setCurrentInstance(row);
                statusDisclosure.onOpen();
              },
              icon: <SearchIcon />,
            },
          ]
        : []),
      {
        label: 'Configure context',
        icon: <TagIcon />,
        onClick: e => {
          onClickActionButton(e, row);
          setIsOpenAddContext(true);
        },
      },
      {
        label: 'Exclude Policies',
        icon: <ExceptionIcon />,
        onClick: e => {
          onClickActionButton(e, row);
          const index = getTabIndex(row?.native_name, MetadataTabs.Alerts);
          updateActiveResource({
            resourceType: row?.native_name as NativeResources,
            uuid: row.uuid,
            accountId: row.account,
            resourceId: row.resource_id,
            cloudProvider: row?.provider,
          });
          onOpenMetadata(index);
        },
      },
    ];
    return (
      <Center onClick={e => e.stopPropagation()}>
        <TableActionsMenu menuItems={items} />
      </Center>
    );
  };

  let columns: TableColumnProps[];

  const nameCloudId = {
    header: <Box pl="thLeftPaddingWithIcon">Name</Box>,
    accessor: '',
    render: ({ row }) => (
      <WithResourceIcon
        resourceType={instanceResourceType}
        iconSize="small"
        iconTooltip={
          <ResourceTypeIconTooltip resourceType={instanceResourceType} />
        }
      >
        <StackedCell upper={row?.name} lower={row?.resource_id} />
      </WithResourceIcon>
    ),
    align: 'left',
  };
  /*const entityType = {
    header: 'Entity Type',
    accessor: 'native_name',
    render: ({ value }) => (
      <>
        {renderResourceType({
          type: value,
          resourceTypes: resourceTypes.data,
        })}
      </>
    ),
    align: 'left',
  };*/

  const regionVpc = {
    header: 'Deployed In',
    align: 'left',
    render: ({ row }) => (
      <StackedCell upper={row?.vpc ?? row?.vpc_id} lower={row?.region} />
    ),
  };
  const deployedInAccount = {
    header: 'Deployed In',
    align: 'left',
    render: ({ row }) => {
      return renderStackedAccountNameID(row?.account);
    },
  };
  const account = {
    header: 'Cloud Account',
    align: 'left',
    render: ({ row }) => {
      return renderStackedAccountNameID(row?.account);
    },
  };
  const ownedBy = {
    header: 'Owner',
    accessor: 'owner',
    align: 'left',
    styles: { width: '60px', cell: { maxWidth: '60px' } },
  };

  const getTruncateLength = () => {
    switch (instanceResourceType) {
      case NativeResources.Ec2Instance:
        return 10;
      default:
        return 15;
    }
  };

  const context = {
    header: 'Context',
    align: 'left',
    render: ({ row }) => renderContextTags(row, getTruncateLength()),
  };

  const riskContext = {
    header: 'Risk Context',
    accessor: 'risk_context',
    align: 'left',
    render: ({ row }) =>
      renderRiskContext(
        sharedRiskContext.data?.[row?.uuid] ??
          row?.risk_context ??
          row?.riskContext ??
          row?.risk_contexts,
      ),
  };

  const actions = {
    header: 'Action',
    accessor: '',
    render: renderActions,
    styles: { width: '60px', cell: { maxWidth: '60px' } },
  };

  const renderVulnerabilitiesLoading = label => (
    <HStack>
      <Box>{label}</Box>
      {instanceAnalysisSummary.isLoading && !instanceInventory.isLoading && (
        <CustomSpinner loading={instanceAnalysisSummary.isLoading} />
      )}
    </HStack>
  );

  const os = {
    header: renderVulnerabilitiesLoading('OS'),
    accessor: 'os_name',
    align: 'left',
    styles: { width: '150px', cell: { maxWidth: '150px' } },
    render: ({ row }) => (
      <StackedCell
        upper={summary?.[row?.uuid]?.os_name || '-'}
        lower={summary?.[row?.uuid]?.os_version || '-'}
      />
    ),
  };

  // Removed the unused `scanningStatus` column (never added to a layout).
  const technology = {
    header: renderVulnerabilitiesLoading('Technology'),
    accessor: 'technologies',
    styles: { width: '60px', cell: { maxWidth: '60px' } },
    render: ({ row }) =>
      summary?.[row?.uuid]?.applications?.length ? (
        <CustomTooltip
          label={
            <Stack>
              {/* Keyed so React can reconcile the application list. */}
              {map(summary?.[row?.uuid]?.applications, (o, index) => (
                <Box key={`${o}-${index}`}>{o}</Box>
              ))}
            </Stack>
          }
        >
          {/* Dropped an unreachable `?? 0`: this branch only renders with a non-zero length. */}
          {summary?.[row?.uuid]?.applications?.length}
        </CustomTooltip>
      ) : (
        0
      ),
  };

  const vulnerabilities = {
    header: renderVulnerabilitiesLoading('Vulnerabilities'),
    accessor: 'vulnerabilities',
    align: 'left',
    render: ({ row }) =>
      renderVulnerabilities({
        value: summary?.[row?.uuid]?.vulnerabilities,
      }),
  };

  // Removed the unused `cloud` column (never added to a layout).
  const tags = {
    header: 'Tags',
    accessor: 'tags',
    render: ({ value }) =>
      !!value?.length ? (
        <CustomTooltip
          label={
            <Stack>
              {map(value, o => renderTooltipFieldValue(o?.key, o?.value))}
            </Stack>
          }
        >
          {value?.length}
        </CustomTooltip>
      ) : (
        0
      ),
    styles: { width: '90px', cell: { maxWidth: '90px' } },
  };

  // Removed the unused `persona` and `account_locator` columns (never added to a layout).
  const created_modified = [
    { header: 'Created', accessor: '', align: 'left' },
    {
      header: 'Modified',
      accessor: 'last_modified',
      align: 'left',
      render: renderTime,
    },
  ];

  const commonColumns = [nameCloudId];

  const ecs = [
    deployedInAccount,
    os,
    technology,
    vulnerabilities,
    context,
    tags,
    actions,
  ];

  const eks = [deployedInAccount, vulnerabilities, context, tags, actions];

  const ecsService = [deployedInAccount, regionVpc, tags, actions];

  const virtualMachine = [
    account,
    ...(isDevOrTest
      ? [
          {
            header: 'State',
            accessor: 'state',
            align: 'left',
            render: ({ row }) => renderResourceState(row),
          },
        ]
      : []),
    os,
    technology,
    vulnerabilities,
    context,
    actions,
  ];

  const serverless = [
    account,
    {
      header: 'Runtime',
      accessor: 'runtime',
      align: 'left',
    },
    vulnerabilities,
    context,
    actions,
  ];

  const image = [account, vulnerabilities, context, actions];

  const ecr = [
    deployedInAccount,
    //vulnerabilities,
    {
      header: 'Type',
      align: 'left',
      render: ({ row }) => (!!row?.public ? 'Public' : 'Private'),
    },
    {
      header: 'URL / Registry',
      render: ({ row }) => (
        <StackedCell upper={row?.repository_uri} lower={row?.registry_id} />
      ),
      align: 'left',
    },
    {
      header: 'Immutable',
      accessor: 'image_tag_mutability',
      render: ({ value }) => (value ? 'Yes' : 'No'),
      align: 'left',
    },
    {
      header: 'Images',
      render: ({ row }) => row?.repository_image_count ?? 0,
      //align: 'left',
    },
    context,
    actions,
  ];

  const database = [
    account,
    { header: 'Technology', accessor: '', align: 'left' },
    { header: 'Deployment type', accessor: '', align: 'left' },
    context,
    tags,
    actions,
  ];

  const user = [
    { header: 'Unique Name', accessor: 'arn', align: 'left' },
    deployedInAccount,
    context,
    tags,
    /*{ header: 'Last Activity', accessor: '', align: 'left' },*/
    actions,
  ];

  const other = [account, context, tags, actions];

  const data = [
    ...commonColumns,
    deployedInAccount,
    /*{
      header: 'PII',
      accessor: 'contexts',
      align: 'left',
      render: ({ row }) => {
        let pii = findPath(
          [...row?.user_tags, ...row?.contexts],
          o => o.key === 'pii',
        );
        if (!!pii) return pii?.value === 'false' ? 'No' : 'Yes';
        return '-';
      },
    },*/
    context,
    tags,
    actions,
  ];

  const snowflakeRole = [
    { header: 'Role Type', accessor: '', align: 'left' },
    { header: 'Privileges', accessor: '', align: 'left' },
    deployedInAccount,
    ...created_modified,
  ];
  const snowflakeUser = [
    deployedInAccount,
    ...created_modified,
    { header: 'Status', accessor: '', align: 'left' },
  ];
  const schemas = {
    header: 'Schemas',
    accessor: 'schemas',
    render: ({ row }) =>
      row?.schemas?.length ?? row?.schemas_count ?? row?.schema_count ?? 0,
  };
  const tables = {
    header: 'Tables',
    accessor: 'tables',
    render: ({ row }) =>
      row?.tables?.length ?? row?.tables_count ?? row?.table_count ?? 0,
  };
  const snowflakeDatabase = [
    deployedInAccount,
    schemas,
    tables,
    context,
    riskContext,
    //...created_modified,
    actions,
  ];
  const snowflakeTable = [
    deployedInAccount,
    context,
    riskContext,
    { header: 'Size', render: ({ row }) => '-' },
    tags,
    actions,
  ];
  const snowflakeOther = [deployedInAccount, context, tags, actions];

  //switch (getResourceType(instanceResourceType)?.cloud_agnostic_class) {
  switch (instanceResourceType) {
    case NativeResources.DocumentDb:
    case NativeResources.DynamoDb:
    case NativeResources.RelationalDatabaseService:
    case NativeResources.Neptune:
      columns = database;
      break;
    case NativeResources.Ec2Instance:
    case NativeResources.Container:
    case NativeResources.GcpComputeEngine:
    case NativeResources.GcpCloudFunctions:
    case NativeResources.GcpContainer:
    case NativeResources.AzureVirtualMachine:
    case NativeResources.AzureContainer:
    case NativeResources.AzureVirtualMachineScaleSets:
    case NativeResources.AzureVirtualMachineScaleSetInstance:
      columns = virtualMachine;
      break;
    case NativeResources.LambdaFunction:
    case NativeResources.AzureFunctions:
      columns = serverless;
      break;
    case NativeResources.AmazonMachineImage:
      columns = image;
      break;
    case NativeResources.ElasticContainerService:
    case NativeResources.EcsCluster:
    case NativeResources.Gcpgke:
      columns = ecs;
      break;
    case NativeResources.ElasticKubernetesService:
    case NativeResources.Aks:
      columns = eks;
      break;
    case NativeResources.IamUser:
    case NativeResources.OktaUser:
    case NativeResources.AzureAdUser:
    case NativeResources.AccountRoot:
    case NativeResources.IamRole:
    case NativeResources.IamGroup:
    case NativeResources.OktaGroup:
    case NativeResources.AzureAdGroup:
      columns = user;
      break;
    case NativeResources.ElasticContainerRegistry:
      columns = ecr;
      break;
    case NativeResources.SnowflakeRoles:
      columns = snowflakeRole;
      break;
    case NativeResources.SnowflakeUsers:
      columns = snowflakeUser;
      break;
    case NativeResources.SnowflakeDatabases:
      columns = snowflakeDatabase;
      break;
    case NativeResources.SnowflakeTables:
      columns = snowflakeTable;
      break;
    case NativeResources.SnowflakeSchemas:
    case NativeResources.SnowflakePrivileges:
      columns = snowflakeOther;
      break;
    case NativeResources.EcsService:
      //case NativeResources.EcsTask:
      columns = ecsService;
      break;
    default:
      columns = other;
  }

  let tableColumns = [...commonColumns, ...columns];
  if (
    currentCategory.split('_')[0] === 'Data' &&
    !instanceResourceType?.startsWith('Snowflake')
  )
    tableColumns = data;

  return {
    tableColumns,
    openWhitelist,
    context,
    created_modified,
    ownedBy,
    summarizeResource,
    statusDisclosure,
  };
};
